/*
 * Figma `95:2` — Date / Time field, and the On-site Settings times at `121:190`.
 *
 * Native `date`, `time` and `datetime-local` inputs. The console's operational screens are edited on
 * show day, on a phone, by someone holding a radio — the platform picker is both faster and the one
 * that already speaks Thai locale formatting without a library.
 *
 * `min`/`max` are passed through deliberately: door time before show start, a milestone before the
 * event date. Bounding the control is how the design prevents the impossible value rather than
 * explaining it after a failed save.
 */
import { useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import { IconWarning } from './icons';

export interface DateTimeFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  mode?: 'date' | 'time' | 'datetime';
  error?: string | null;
  hint?: string;
  requiredMark?: boolean;
}

const INPUT_TYPE = { date: 'date', time: 'time', datetime: 'datetime-local' } as const;

export function DateTimeField({
  label, mode = 'date', error, hint, requiredMark, className = '', ...rest
}: DateTimeFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="jt-field">
      <label className="jt-field-label" htmlFor={id}>
        {label}
        {requiredMark && <span className="jt-field-required" aria-hidden="true">*</span>}
      </label>
      <input
        {...rest}
        id={id}
        type={INPUT_TYPE[mode]}
        className={`jt-input jt-datetime ${className}`.trim()}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {hint && <div className="jt-field-hint" id={hintId}>{hint}</div>}
      {error && (
        <div className="jt-field-error" id={errorId}>
          <IconWarning size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
